import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import GlitchPrismScene from './GlitchPrismScene';
import { getCoreAppearance } from './coreAppearance';
import { CORE_PHASE_RANK } from './corePhases';
import { CORE_PHASE_SPELL_MS, getCoreSpellRevealAt } from './coreBoardPhases';
import { getBoardSquareLabel } from './doorBoard';
import { playSound } from '../../utils/utils';
import { lockBodyScroll } from '../../utils/bodyScrollLock';
import './CorePhaseSpell.css';

const CorePhaseSpell = ({ game, spell, reduceMotion, onComplete }) => {
  const [cast, setCast] = useState(false);
  const [webgl, setWebgl] = useState(true);
  const [revealedTiles, setRevealedTiles] = useState([]);
  const [startedAt, setStartedAt] = useState(null);
  const complete = useRef(onComplete);
  const skip = useRef(null);
  const finished = useRef(false);
  complete.current = onComplete;
  const finish = () => {
    if (finished.current) return;
    finished.current = true;
    complete.current(spell.id);
  };
  const beforeBoard = useMemo(() => {
    const board = [...game.doorBoard];
    spell.changes.forEach(({ index, before }) => {
      board[index] = before;
    });
    return board;
  }, [game.doorBoard, spell]);
  const visualSpell = useMemo(
    () => ({
      ...spell,
      startedAt: startedAt ?? Infinity,
      duration: reduceMotion ? 650 : CORE_PHASE_SPELL_MS,
    }),
    [spell, reduceMotion, startedAt]
  );
  useEffect(() => {
    const previousFocus = document.activeElement;
    const unlock = lockBodyScroll();
    // Start after the board's first frame has compiled its shaders.
    setStartedAt(Date.now());
    skip.current?.focus({ preventScroll: true });
    const revealTimers = [];
    let stopTileSound;
    if (!reduceMotion) {
      spell.changes.forEach(({ index }) => {
        revealTimers.push(
          setTimeout(() => {
            setRevealedTiles((current) => [...current, index]);
            stopTileSound?.();
            stopTileSound = playSound('/sounds/menu-glitch-select-option.mp3', {
              preventOverlap: true,
            });
          }, getCoreSpellRevealAt(spell, index) * visualSpell.duration)
        );
      });
    }
    const castTimer = setTimeout(
      () => {
        setCast(true);
        if (reduceMotion || !spell.changes.length)
          playSound('/sounds/glitch_core_misc_sound_2.mp3', {
            preventOverlap: true,
          });
      },
      reduceMotion ? 100 : 850
    );
    const timer = setTimeout(() => {
      if (finished.current) return;
      finished.current = true;
      complete.current(spell.id);
    }, visualSpell.duration);
    return () => {
      clearTimeout(castTimer);
      clearTimeout(timer);
      revealTimers.forEach(clearTimeout);
      stopTileSound?.();
      unlock();
      if (previousFocus?.isConnected) previousFocus.focus?.();
    };
  }, [spell, reduceMotion, visualSpell.duration]);
  const rowSpells = spell.spells.filter(({ row }) => row !== null);
  const rowCopy = rowSpells.length
    ? `Corrupting beneficial tiles in ${
        rowSpells.length > 1 ? 'rows' : 'row'
      } ${rowSpells.map(({ row }) => row + 1).join(' and ')}.`
    : 'The Core corrupts more of your beneficial tiles.';
  const level = CORE_PHASE_RANK[spell.phase];
  const massiveCount = game.bossDoorBoard.filter(
    (s) => s.effect?.type === 'massiveAttack'
  ).length;
  const powerCount = game.bossDoorBoard.filter(
    (s) => s.effect?.type === 'corePower'
  ).length;
  return createPortal(
    <div
      className={`coreboundPhaseSpell ${cast ? 'is-casting' : ''} ${
        reduceMotion ? 'is-reduced' : ''
      }`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="corebound-phase-spell-title"
      aria-describedby="corebound-phase-spell-rule"
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          finish();
        }
        if (event.key === 'Tab') {
          event.preventDefault();
          skip.current?.focus();
        }
      }}
    >
      <header>
        <small>{getCoreAppearance(spell.phase).label}</small>
        <h2 id="corebound-phase-spell-title">
          {getCoreAppearance(spell.phase).transitionName}
        </h2>
        <p>{rowCopy}</p>
      </header>
      <div className="coreboundPhaseSpellArena">
        <GlitchPrismScene
          phase="setup"
          sector={game.floor}
          dieType={game.selectedDie}
          reduceMotion={reduceMotion}
          corePhase={spell.phase}
          doorBoard={beforeBoard}
          coreBoss={{
            hp: game.bossHp,
            hideReadout: true,
            phase: spell.phase,
            stage: 'phaseSpell',
            spell: visualSpell,
          }}
          onWebglAvailabilityChange={setWebgl}
        />
        {!webgl && (
          <div
            className="coreboundPhaseSpellFallback"
            aria-label="Player board corruption preview"
          >
            <div className="coreboundPhaseSpellHeart" aria-hidden="true">
              ◆
            </div>
            <div
              className="coreboundPhaseSpellGrid"
              role="grid"
              aria-label="Your changed board"
            >
              {beforeBoard.map((before, index) => {
                const change = spell.changes.find(
                  (entry) => entry.index === index
                );
                const square =
                  change &&
                  (reduceMotion ? cast : revealedTiles.includes(index))
                    ? change.after
                    : before;
                return (
                  <span
                    role="gridcell"
                    key={index}
                    className={
                      square.effect?.type === 'corruption' ? 'is-corrupted' : ''
                    }
                    aria-label={`Square ${index + 1}: ${getBoardSquareLabel(
                      square
                    )}`}
                  >
                    {getBoardSquareLabel(square)}
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </div>
      <footer>
        <p id="corebound-phase-spell-rule">
          <strong>CORRUPTED · −100 ROLL</strong>
          Aim around corrupted tiles. They remain for the rest of this fight.
        </p>
        <p className="coreboundPhaseSpellUpgrade">
          CORE BOARD UPGRADED
          <span>
            {powerCount > 0
              ? `${powerCount} power tiles: +${level * 20}% damage`
              : 'Power upgrade on future Core boards'}{' '}
            · {massiveCount} MASSIVE ATTACK tiles
          </span>
        </p>
        <button ref={skip} type="button" onClick={finish}>
          Skip spell
        </button>
      </footer>
    </div>,
    document.body
  );
};

export default CorePhaseSpell;
